import React, { useState } from 'react';
import { X, ZoomIn, ZoomOut, Maximize2, Minimize2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import AuthImage from './AuthImage';

/**
 * Componente profesional para visualizar imágenes con zoom y pantalla completa
 */
const ImageViewer = ({ src, alt, className, containerClassName }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);

  const handleZoomIn = () => {
    setZoom(Math.min(zoom + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(Math.max(zoom - 0.25, 0.5));
  };

  const handleFullscreen = () => {
    setIsFullscreen(true);
    setZoom(1);
  };

  const handleCloseFullscreen = () => {
    setIsFullscreen(false);
    setZoom(1);
  };

  return (
    <>
      {/* Vista normal con hover para maximizar */}
      <div className={`group relative ${containerClassName || ''}`}>
        <AuthImage 
          src={src} 
          alt={alt} 
          className={`${className} transition-transform duration-200`}
        />
        
        {/* Overlay con botón de maximizar */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-200 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              handleFullscreen();
            }}
            className="p-3 bg-white/90 hover:bg-white rounded-full shadow-lg transform scale-90 group-hover:scale-100 transition-all"
            title="Ver en pantalla completa"
          >
            <Maximize2 className="w-5 h-5 text-neutral-700" />
          </button>
        </div>
      </div>

      {/* Modal de pantalla completa */}
      <AnimatePresence>
        {isFullscreen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center"
            onClick={handleCloseFullscreen}
          >
            {/* Controles superiores */}
            <div className="absolute top-0 left-0 right-0 p-4 bg-gradient-to-b from-black/80 to-transparent z-10">
              <div className="flex items-center justify-between max-w-7xl mx-auto">
                <div className="flex items-center gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleZoomOut();
                    }}
                    disabled={zoom <= 0.5}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Alejar"
                  >
                    <ZoomOut className="w-5 h-5 text-white" />
                  </button>
                  <span className="text-white font-medium min-w-[60px] text-center">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleZoomIn();
                    }}
                    disabled={zoom >= 3}
                    className="p-2 bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Acercar"
                  >
                    <ZoomIn className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setZoom(1);
                    }}
                    className="px-3 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm font-medium transition-colors"
                    title="Restablecer zoom"
                  >
                    Restablecer
                  </button>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseFullscreen();
                  }}
                  className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  title="Cerrar"
                >
                  <X className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>

            {/* Contenedor de la imagen con zoom */}
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="relative max-w-7xl max-h-[90vh] overflow-auto p-4"
              onClick={(e) => e.stopPropagation()}
            >
              <AuthImage 
                src={src} 
                alt={alt} 
                className="w-full h-auto object-contain rounded-lg shadow-2xl"
                style={{ transform: `scale(${zoom})`, transition: 'transform 0.3s ease' }}
              />
            </motion.div>

            {/* Instrucciones */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full">
              <p className="text-white text-sm">
                Click fuera de la imagen para cerrar • Usa los controles para hacer zoom
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ImageViewer;
